// WeightEditDialog.js (create this as a new file in your components folder)
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  alpha,
  useTheme
} from '@mui/material';

const WeightEditDialog = ({ 
  open, 
  onClose, 
  item, 
  onConfirm 
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [weight, setWeight] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && item) {
      setWeight(item.currentWeight?.toString() || '');
      setError('');
    }
  }, [open, item]);

  const handleConfirm = () => {
    const newWeight = parseFloat(weight);
    if (isNaN(newWeight) || newWeight <= 0) {
      setError('Please enter a valid weight greater than 0');
      return;
    }
    
    if (Math.abs(newWeight - item.originalStockWeight) < 0.001) {
      onClose();
      return;
    }
    
    onConfirm(newWeight);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: 'blur(20px)',
          background: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Edit Item Weight
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Item: {item.name}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Original Stock Weight: <strong>{item.originalStockWeight}g</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Current Bill Weight: <strong>{item.currentWeight}g</strong>
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Changing the weight will require a stock adjustment. Please enter the new weight below.
        </Alert>
        
        <TextField
          label="New Weight (g)"
          type="number"
          value={weight}
          onChange={(e) => {
            setWeight(e.target.value);
            setError('');
          }}
          onKeyPress={handleKeyPress}
          fullWidth
          autoFocus
          error={!!error}
          helperText={error}
          inputProps={{ step: "0.01", min: "0.01" }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : '#ffffff',
                isDark ? 0.2 : 0.7
              ),
            }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">g</InputAdornment>,
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          }}
        >
          Confirm Change
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeightEditDialog;